import { create } from '@bufbuild/protobuf';

import { IBooleanResponse } from '@gem/models/commonModel';
import { logger } from '@gem/modules/logger';

import { mapDevicesKeyPackagesCountResponse } from 'APP/model/auth/authModel.mapper';
import { IDeviceKeyPackagesCount } from 'APP/model/auth/authModel.types';
import { mapGroupCounterView } from 'APP/model/counter/counterModel.b2b.mapper';
import {
  keyPackageAlgorithmRequestMap,
  mapAcceptSecretChatAccessRequestsRequest,
  mapActiveSecretChatAccessRequestsResponse,
  mapAddSecretGroupParticipantsRequest,
  mapCommitsResponse,
  mapCreateCommitRequest,
  mapCreateSecretGroupAndInviteRequest,
  mapKeyPackageRequests,
  mapKeyPackagesForUntrustedResponse,
  mapKeyPackagesResponse,
  mapRemoveSecretGroupParticipantsRequest,
  mapWelcomesResponse,
} from 'APP/model/e2ee/e2eeModel.mapper';
import { buildModelError } from 'APP/model/error/errorBuilder';
import { WsRequestError } from 'APP/model/error/errors/WsRequestError';
import { mapPersonalizedChatResponse } from 'APP/model/group/groupModel.b2b.mapper';
import { IGroupWithBlockStatusResponse } from 'APP/model/group/groupModel.types';
import { ServerErrorCode } from 'APP/restApi';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import {
  consumeKeyPackagesRequest,
  uploadKeyPackagesRequest,
  createSecretChatRequest,
  getWelcomesRequest,
  ackWelcomesRequest,
  getCommitsRequest,
  addSecretChatParticipantsRequest,
  removeSecretChatParticipantsRequest,
  uploadKeyPackagesUntrustedRequest,
  getKeyPackagesForUntrustedDeviceRequest,
  createCommitRequest,
  createSecretChatRequestAccessRequest,
  getActiveSecretChatAccessRequestsRequest,
  consumeKeyPackagesByUserIdAndDeviceIdRequest,
  acceptSecretChatAccessRequestsRequest,
  declineSecretChatAccessRequestsRequest,
  getKeyPackagesCountRequest,
  clearCurrentDeviceE2eeDataRequest,
} from 'APP/wsApi/wsAppApi/requests/e2eeRequests';
import { DeclineSecretChatAccessRequestsRequest_UserDeviceSchema } from 'APP/wsApi/wsAppApi/types/chat/chat_requests_pb';

import {
  IAddSecretGroupParticipantsData,
  ICreateCommitForSecretGroupData,
  ICreateSecretChatData,
  IDeclineSecretChatAccessRequestsData,
  IE2eeCommitResponse,
  IE2eeWelcomeResponse,
  IKeyPackage,
  ISecretChatAccessRequest,
  ISecretGroupParticipantChangeRequest,
  IUserDeviceKeyPackages,
  IUserDeviceKeyPackagesResponse,
  KeyPackageAlgorithm,
} from './e2eeModel.types';

export const uploadKeyPackages = async (
  keyPackages: IKeyPackage[],
  isUntrusted?: boolean
): Promise<void> => {
  try {
    const request = isUntrusted ? uploadKeyPackagesUntrustedRequest : uploadKeyPackagesRequest;
    const result = await request(mapKeyPackageRequests(keyPackages));

    if (result.type.case !== 'success') {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.uploadKeyPackages', error);
    throw buildModelError(error);
  }
};

export const consumeKeyPackages = async (membersIds: string[]): Promise<IUserDeviceKeyPackages> => {
  try {
    const body = {
      userIds: membersIds.map((item) => BigInt(item)),
      algorithm: keyPackageAlgorithmRequestMap[KeyPackageAlgorithm.CURVE_25519],
    };

    const result = await consumeKeyPackagesRequest(body);

    if (result.type.case === 'success') {
      return mapKeyPackagesResponse(result.type.value.keyPackages);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.consumeKeyPackages', error);
    throw buildModelError(error);
  }
};

export const consumeKeyPackagesByDeviceIdAndUserId = async (
  deviceId: string,
  userId: string,
  count: number
): Promise<IUserDeviceKeyPackages> => {
  try {
    const result = await consumeKeyPackagesByUserIdAndDeviceIdRequest({
      algorithm: keyPackageAlgorithmRequestMap[KeyPackageAlgorithm.CURVE_25519],
      userId: BigInt(userId),
      deviceId,
      count,
    });

    if (result.type.case === 'success') {
      return mapKeyPackagesResponse(result.type.value.keyPackages);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.consumeKeyPackagesByDeviceIdAndUserId', error);
    throw buildModelError(error);
  }
};

export async function createSecretGroupAndInvite(
  data: ICreateSecretChatData
): Promise<IGroupWithBlockStatusResponse> {
  try {
    const result = await createSecretChatRequest(mapCreateSecretGroupAndInviteRequest(data));

    switch (result.type.case) {
      case 'userDoesNotExist':
        throw new WsRequestError(ServerErrorCode.BadData, result);
      case 'chatBadData':
        throw new WsRequestError(ServerErrorCode.BadData, result);
      case 'e2eeParticipantMissingWelcome':
        throw new WsRequestError(ServerErrorCode.BadData, result);
    }

    return {
      personalizedGroup: mapPersonalizedChatResponse(result.type.value!.personalizedChat!),
      groupCounter: mapGroupCounterView(result.type.value!.chatUpdate!),
      usersFailedToInvite: [],
    };
  } catch (error) {
    logger.get('API').error('e2eeModel.createSecretGroupAndInvite', error);
    throw buildModelError(error);
  }
}

export async function addSecretGroupParticipants(
  data: IAddSecretGroupParticipantsData
): Promise<IBooleanResponse & { needToSyncCommits?: boolean }> {
  try {
    const result = await addSecretChatParticipantsRequest(
      mapAddSecretGroupParticipantsRequest(data)
    );

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'userDoesNotExist':
        throw new WsRequestError(ServerErrorCode.UserDoesNotExist, result);
      case 'e2eeCommitSyncRequired':
        return { result: false, needToSyncCommits: true };
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.addSecretGroupParticipants', error);
    throw buildModelError(error);
  }
}

export async function removeSecretGroupParticipants(
  data: ISecretGroupParticipantChangeRequest
): Promise<IBooleanResponse & { needToSyncCommits?: boolean }> {
  try {
    const result = await removeSecretChatParticipantsRequest(
      mapRemoveSecretGroupParticipantsRequest(data)
    );

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'e2eeCommitSyncRequired':
        return { result: false, needToSyncCommits: true };
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.removeSecretGroupParticipants', error);
    throw buildModelError(error);
  }
}

export const getWelcomes = async (): Promise<IE2eeWelcomeResponse> => {
  try {
    const result = await getWelcomesRequest({});

    if (result.type.case === 'success') {
      return mapWelcomesResponse(result.type.value);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.getWelcomes', error);
    throw buildModelError(error);
  }
};

export const ackWelcomes = async (chatIds: string[]): Promise<void> => {
  try {
    const result = await ackWelcomesRequest({ chatIds: chatIds.map((item) => BigInt(item)) });

    if (result.type.case === 'success') {
      return;
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.ackWelcomes', error);
    throw buildModelError(error);
  }
};

export const getCommits = async (epoch: number, chatId: string): Promise<IE2eeCommitResponse> => {
  try {
    const result = await getCommitsRequest({
      afterEpoch: BigInt(epoch),
      chatId: BigInt(chatId),
    });

    if (result.type.case === 'success') {
      return mapCommitsResponse(result.type.value);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.getCommits', error);
    throw buildModelError(error);
  }
};

export const getKeyPackagesForUntrustedDevice = async (
  deviceId: string,
  limit: number,
  cursor?: string
): Promise<IUserDeviceKeyPackagesResponse> => {
  try {
    const result = await getKeyPackagesForUntrustedDeviceRequest({
      deviceId,
      limit,
      cursor,
      algorithm: keyPackageAlgorithmRequestMap[KeyPackageAlgorithm.CURVE_25519],
    });

    if (result.type.case === 'success') {
      return mapKeyPackagesForUntrustedResponse(result.type.value);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.getKeyPackagesForUntrustedDevice', error);
    throw buildModelError(error);
  }
};

export async function createSecretGroupCommit(
  data: ICreateCommitForSecretGroupData
): Promise<IBooleanResponse & { needToSyncCommits?: boolean }> {
  try {
    const result = await createCommitRequest(mapCreateCommitRequest(data));

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'e2eeCommitSyncRequired':
        return { result: false, needToSyncCommits: true };
      case 'e2eeCommitResyncRequired':
        return { result: false, needToSyncCommits: true };
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.createSecretGroupCommit', error);
    throw buildModelError(error);
  }
}

export async function acceptSecretChatAccessRequests(
  data: IAddSecretGroupParticipantsData
): Promise<IBooleanResponse & { needToSyncCommits?: boolean }> {
  try {
    const result = await acceptSecretChatAccessRequestsRequest(
      mapAcceptSecretChatAccessRequestsRequest(data)
    );

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'e2eeCommitSyncRequired':
        return { result: false, needToSyncCommits: true };
      case 'e2eeCommitResyncRequired':
        return { result: false, needToSyncCommits: true };
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.acceptSecretChatAccessRequests', error);
    throw buildModelError(error);
  }
}

export async function declineSecretChatAccessRequests(
  data: IDeclineSecretChatAccessRequestsData
): Promise<IBooleanResponse> {
  try {
    const result = await declineSecretChatAccessRequestsRequest({
      chatId: BigInt(data.groupId),
      userDevice: [
        create(DeclineSecretChatAccessRequestsRequest_UserDeviceSchema, {
          userId: BigInt(data.userId),
          deviceId: data.deviceId,
        }),
      ],
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.declineSecretChatAccessRequests', error);
    throw buildModelError(error);
  }
}

export async function createSecretChatRequestAccess(groupId: string): Promise<IBooleanResponse> {
  try {
    const result = await createSecretChatRequestAccessRequest({
      chatId: BigInt(groupId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'invalidChatType':
        throw new WsRequestError(GroupServerErrorCode.InvalidChatType, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('e2eeModel.createSecretChatRequestAccess', error);
    throw buildModelError(error);
  }
}

export async function getActiveSecretChatAccessRequests(
  groupId: string
): Promise<ISecretChatAccessRequest[]> {
  try {
    const result = await getActiveSecretChatAccessRequestsRequest({
      chatId: BigInt(groupId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    if (result.type.case !== 'success') {
      throw new Error('Unexpected error');
    }

    return result.type.value.secretChatAccessRequest.map(mapActiveSecretChatAccessRequestsResponse);
  } catch (error) {
    logger.get('API').error('e2eeModel.getActiveSecretChatAccessRequests', error);
    throw buildModelError(error);
  }
}

export const getKeyPackagesCount = async (): Promise<IDeviceKeyPackagesCount | null> => {
  try {
    const result = await getKeyPackagesCountRequest({});

    if (result.type.case === 'success') {
      return mapDevicesKeyPackagesCountResponse(result.type.value.deviceKeyPackagesCount);
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.getKeyPackagesCount', error);
    throw buildModelError(error);
  }
};

export const clearCurrentDeviceE2eeData = async (): Promise<IBooleanResponse> => {
  try {
    const result = await clearCurrentDeviceE2eeDataRequest({});

    if (result.type.case === 'success') {
      return { result: result.type.value.success };
    } else {
      throw new Error('Unexpected error');
    }
  } catch (error) {
    logger.get('API').error('e2eeModel.clearCurrentDeviceE2eeData', error);
    throw buildModelError(error);
  }
};
